/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React from 'react';
import { useTheme } from '../../theme/ThemeContext';

function StackSection() {
    const { theme } = useTheme();

    return (
        <section className="py-5 bg-white">
            <div className="container py-5">
                <div className="row align-items-center">
                    <div className="col-lg-5 mb-5 mb-lg-0">
                        <h2 className="fw-bold display-4 mb-4" style={{ color: theme.primary }}>
                            Standardize Your Stack
                        </h2>
                        <p className="lead text-muted mb-4">
                            Fragmented tools create chaos. <span className="fw-bold text-dark">arcee unifies your application development</span> on a single, scalable standard.
                        </p>
                        <div className="d-flex flex-column gap-3">
                            <div className="d-flex align-items-start">
                                <i className="bi bi-check-circle-fill text-success fs-4 me-3 mt-1"></i>
                                <div>
                                    <h5 className="fw-bold mb-1">Open Source Core</h5>
                                    <p className="text-muted small mb-0">Built on standard Django & React. Extend code anytime.</p>
                                </div>
                            </div>
                            <div className="d-flex align-items-start">
                                <i className="bi bi-check-circle-fill text-success fs-4 me-3 mt-1"></i>
                                <div>
                                    <h5 className="fw-bold mb-1">One Backend, Many Apps</h5>
                                    <p className="text-muted small mb-0">Don't manage 50 databases. Manage one arcee instance.</p>
                                </div>
                            </div>
                            <div className="d-flex align-items-start">
                                <i className="bi bi-check-circle-fill text-success fs-4 me-3 mt-1"></i>
                                <div>
                                    <h5 className="fw-bold mb-1">AI Configuration</h5>
                                    <p className="text-muted small mb-0">Chat with arcee to build your flows instantly.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-7">
                        {/* Visual representation of stack unification */}
                        <div className="p-5 rounded-4" style={{ background: theme.gradientBackground, color: 'white' }}>
                            <div className="text-center mb-4">
                                <span className="badge bg-white text-dark mb-2">Before Arcee</span>
                                <div className="d-flex justify-content-center flex-wrap gap-2 opacity-75">
                                    <span className="border border-white p-2 rounded">Retool</span>
                                    <span className="border border-white p-2 rounded">Bubble</span>
                                    <span className="border border-white p-2 rounded">Airtable</span>
                                    <span className="border border-white p-2 rounded">Custom Node.js</span>
                                    <span className="border border-white p-2 rounded">Firebase</span>
                                </div>
                            </div>
                            <div className="text-center">
                                <i className="bi bi-arrow-down fs-1 mb-2"></i>
                            </div>
                            <div className="text-center">
                                <span className="badge bg-white text-dark mb-3">With Arcee</span>
                                <h3 className="fw-bold display-6">Unified App Engine</h3>
                                <div className="d-flex justify-content-center gap-4 mt-3">
                                    <div className="text-center">
                                        <i className="bi bi-database fs-2"></i>
                                        <div className="small mt-1">Single DB</div>
                                    </div>
                                    <div className="text-center">
                                        <i className="bi bi-shield-lock fs-2"></i>
                                        <div className="small mt-1">Unified Auth</div>
                                    </div>
                                    <div className="text-center">
                                        <i className="bi bi-code-slash fs-2"></i>
                                        <div className="small mt-1">Standard API</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default StackSection;
